// Shared types between worker and web. Do not import any backend libraries
// (Drizzle, postgres, node modules) here — this package must be safe to
// import from the browser.

// === Enums ===

export type AppRole = 'back_office' | 'owner' | 'admin';

export type CustomerType = 'residential' | 'builder' | 'commercial';

export type JobStatus =
  | 'created'            // job created, awaiting field estimate (initial)
  | 'estimated'          // crew confirmed field estimate done
  | 'quoted'             // office built quote AND sent to customer
  | 'accepted'           // customer accepted (manual office action)
  | 'scheduled'          // job is on the calendar
  | 'installed'          // work complete on site; awaiting invoice-out
  | 'invoiced'           // invoice has been sent to customer; aging payment clock starts from invoiced_at
  | 'completed'          // paid and closed (terminal)
  | 'disqualified';      // sidetracked (preserves prior status in disqualified_from_status)

export type JobType = 'new_work' | 'repair' | 'warranty';

/** Trade / product-line bucket. Drives the Gutter / Insulation tabs
 *  on the Jobs page. Set at job creation; rarely changes. */
export type ProductLine = 'gutter' | 'insulation';

// Visible stages in the stepper UI, in order. 'created' is intentionally
// NOT in this list — it maps visually to "Estimated pending" (idx 0 in
// stagedFromStatus). 'disqualified' renders as a separate badge.
export const JOB_STAGE_FLOW: ReadonlyArray<Exclude<JobStatus, 'created' | 'disqualified'>> = [
  'estimated',
  'quoted',
  'accepted',
  'scheduled',
  'installed',
  'invoiced',
  'completed',
];

// Map a status to its index in JOB_STAGE_FLOW. 'created' maps to -1
// meaning "no stage completed yet, the first stage (Estimated) is pending".
// 'disqualified' has no inherent index — callers should use
// disqualifiedFromStatus to look up the frozen stage.
export function stageIndexFromStatus(
  status: Exclude<JobStatus, 'disqualified'>,
): number {
  if (status === 'created') return -1;
  return JOB_STAGE_FLOW.indexOf(status);
}

// Valid status transitions. Each key maps to the set of statuses it
// can move TO. Notes:
//   - 'created' → 'estimated' is triggered by crew "Confirm done" in the bot.
//   - 'estimated' → 'quoted' is auto-triggered by the office completing
//     BOTH (Build Quote + PDF) AND (Send to Customer). It can also be set
//     manually via dropdown.
//   - 'scheduled' has TWO outbound paths: 'installed' (forward) and
//     'accepted' (auto-revert when the last calendar assignment is removed).
//   - 'installed' → 'awaiting_payment' is the forward path once the office
//     has billed the customer. 'installed' → 'scheduled' stays allowed as a
//     one-step revert for corrections.
//   - 'awaiting_payment' → 'completed' when money is in. Revert to
//     'installed' allowed for corrections.
//   - 'completed' is the terminal state — no outbound transitions.
//   - From 'installed' onward (installed, awaiting_payment, completed),
//     'disqualified' is NOT allowed — once work is done, a job cannot
//     be disqualified; write-off is an accounting concern, not status.
//   - 'disqualified' has no static targets; the reopen target is the
//     value stored in jobs.disqualified_from_status. The dropdown UI
//     should show "Reopen" as the action label.
export const JOB_STATUS_TRANSITIONS: Record<JobStatus, readonly JobStatus[]> = {
  created:          ['estimated', 'disqualified'],
  // 'estimated → created' is the re-do-from-scratch path. Office decides
  // the BoM the bot submitted is wrong and wants to dispatch a fresh
  // estimate visit. Server-side revert clears estimated_by_crew_member_id
  // (suppresses the dashboard activity-feed replay) and NULLs estimate-
  // purpose job_assignments.completed_at so the office can re-dispatch
  // cleanly without orphan dimmed calendar chips. estimated_at left intact
  // — next forward stamp overwrites. Phase 1.5 Item 17/18.
  estimated:        ['quoted', 'created', 'disqualified'],
  // 'quoted → estimated' is the re-quote path: the customer asked
  // for changes after seeing the original PDF, office is iterating on
  // SKUs / amount. Auto-fired by quotes.ts + jobs-transitions.ts when
  // the office edits line items or quote amount in 'quoted' state.
  // Also reachable manually if the office wants to back off a quote
  // explicitly. The frozen PDF stays in place until the next freeze
  // (estimated → quoted) overwrites it.
  quoted:           ['estimated', 'accepted', 'disqualified'],
  // 'accepted → quoted' is the "let me re-issue the quote" path. Customer
  // changed mind after accepting; office wants to reopen iteration.
  // Server-side revert is a no-op (no data corruption) — the quote-
  // freeze handler is gated on fromStatus='estimated', so reverse from
  // accepted preserves the frozen PDF + quote_sent_at intact. Phase 1.5.
  accepted:         ['scheduled', 'quoted', 'disqualified'],
  // 'scheduled → accepted' was previously omitted with this comment:
  //   "scheduled→accepted should ONLY happen via the calendar-removal
  //    auto-revert ... otherwise the office could pick 'Accepted' and
  //    orphan a job_assignment row."
  // Phase 1.5 changes this to a CONDITIONAL allowed transition: the
  // map includes 'accepted' but the server-side handler at
  // jobs-transitions.ts rejects with 409 `assignments_block_revert`
  // when any install-purpose job_assignments rows still exist (covers
  // gutter calendar chips + insulation phase visits — phases write ja
  // rows with purpose='install'). UI greys-out the 'Accepted' option +
  // shows a product-line-specific hint to remove assignments first.
  scheduled:        ['installed', 'accepted', 'disqualified'],
  installed:        ['invoiced', 'scheduled'],
  invoiced:         ['completed', 'installed'],
  // Completed is effectively terminal for forward motion — the only
  // path out is a one-step revert to invoiced for corrections (matches
  // the installed→scheduled revert pattern). Once-completed jobs don't
  // auto-transition anywhere.
  completed:        ['invoiced'],
  disqualified:     [], // dynamic — see jobs.disqualified_from_status
} as const;

export type TrackingLevel = 'rigorous' | 'loose';

export type SkuUnit = 'ft' | 'ea' | 'box' | 'bag' | 'tube' | 'can';

export type FieldEstimateStatus =
  | 'confirmed'
  | 'needs_review'
  | 'rejected'
  | 'pulled'
  | 'returned'
  | 'released';

export type FieldEstimateSource = 'buttons' | 'freehand' | 'voice';

export type FieldEstimateContext = 'quote' | 'install';

export type InventoryTxnReason =
  | 'receipt'
  | 'pull'
  | 'return'
  | 'damage'
  | 'count_correction'
  | 'install'
  | 'uninstall';

// === Entity types (API contract — stable, distinct from DB row shape) ===

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

export interface AppUser {
  id: string;
  tenantId: string;
  email: string;
  role: AppRole;
  active: boolean;
  createdAt: string;
}

export interface CrewMember {
  id: string;
  tenantId: string;
  telegramUserId: number;
  name: string;
  role: string | null;
  active: boolean;
  createdAt: string;
}

export interface Customer {
  id: string;
  tenantId: string;
  type: CustomerType;
  /** Canonical display name. For builder/commercial = companyName; for
   * residential = the contact's own name. Always set for back-compat. */
  name: string;
  /** Org name. NULL for residential, set for builder/commercial. */
  companyName: string | null;
  /** Point-of-contact human inside the org. NULL for residential. */
  contactName: string | null;
  email: string | null;
  phone: string | null;
  discountPct: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  // Aggregated fields populated by GET /api/customers (and the detail
  // endpoint). They are NOT part of the row in the customers table —
  // they're computed at query time via a LEFT JOIN with jobs. Optional
  // because internal callers (e.g. POST /jobs FK validation) don't need
  // them and we leave them undefined to keep the contract narrow.
  jobCount?: number;
  lastJobAt?: string | null;
}

export interface Job {
  id: string;
  tenantId: string;
  customerId: string;
  displayName: string;
  poNumber: string | null;
  address: string;
  jobType: JobType;
  /** Trade bucket: 'gutter' | 'insulation'. */
  productLine: ProductLine;
  status: JobStatus;
  /**
   * When status='disqualified', this holds the status the job had IMMEDIATELY
   * before being disqualified. Used by the stepper UI to render the frozen
   * prior visual + Disqualified badge, AND by the reopen action to route the
   * job back to where it left off. Null in all other states.
   */
  disqualifiedFromStatus: Exclude<JobStatus, 'disqualified'> | null;
  /**
   * Office-set planning hint, captured on job create. Kept for
   * backward compat with the Today's Jobs panel filter (which still
   * matches against this value). NOT shown in displays anymore — UIs
   * should prefer `scheduledStartDate` (derived from real calendar
   * assignments) so a job's "Install: …" only appears when actually
   * scheduled.
   */
  targetInstallDate: string | null;
  /**
   * The earliest `job_assignments.assigned_date` for this job — i.e.
   * when the office actually put it on the calendar. Null when no
   * calendar assignment exists. Use this (not targetInstallDate) for
   * any "Install: …" display so the UI never lies about a job's
   * schedule.
   */
  scheduledStartDate: string | null;
  /** Stamped when status first moves TO 'estimated' (by bot OR by office). */
  estimatedAt: string | null;
  /** Date the invoice was SENT to the customer — not the status-change
   *  timestamp. Captured via modal when office advances
   *  installed → invoiced. Drives the "overdue payment" aging clock. */
  invoicedAt: string | null;
  /** External (QuickBooks) invoice number the office typed in on the
   *  installed → invoiced transition (gutter: one per job; migration 0052).
   *  The app no longer generates invoices — this captures the QBO number.
   *  Null until recorded; cleared on revert invoiced → installed. */
  externalInvoiceNumber: string | null;
  /** Office-recorded amount actually invoiced (cents). Editable override; when
   *  null the UI shows the recommended materials+labor total (quoteTotalCents)
   *  as the default. Migration 0059. Cleared on revert invoiced → installed. */
  invoicedAmountCents: number | null;
  /** Stamped when status moves TO 'completed' (moneys in). */
  closedAt: string | null;
  /**
   * Free-text notes — single chronological field shared between the
   * office (manual) and the bot (auto-appended timestamped lines on
   * crew events: estimate submitted, install confirmed). Last-write-wins
   * via PATCH; UI shows the raw text.
   */
  notes: string | null;
  sketchPhotoR2Key: string | null;
  hasSketchPhoto: boolean;
  quoteAmountCents: number | null;
  /**
   * Frozen sales-tax rate for this job's quote (migration 0048). 0 = tax-free
   * (all new jobs). Legacy jobs carry the rate that was live when they were
   * quoted so their displayed totals stay unchanged after tax was removed.
   * Display code adds tax from THIS, never the company-wide tenant rate.
   */
  quoteTaxRatePctSnapshot: number;
  /**
   * Customer LABOR charge (migration 0054). Gutter is billed per LF (footage
   * derived from ft-unit estimate lines + manual override); insulation per SF
   * (footage hand-entered). Live-until-sent then frozen at estimated→quoted —
   * see pricing/labor.ts. Folded into the total; no separate PDF line.
   */
  /** Effective rate (cents/unit) frozen at send. 0 on un-sent + historical jobs. */
  quoteLaborRateCentsPerUnitSnapshot: number;
  /** Per-job rate override (cents/unit); null = use tenant default. */
  quoteLaborRateCentsPerUnitOverride: number | null;
  /** Effective rate actually applied (cents/unit): live = override ?? tenant,
   *  frozen = snapshot (server-computed; read-only). */
  laborRateCentsPerUnit: number;
  /** Manual footage: gutter LF override (null = derive) / insulation SF (null = none). */
  quoteLaborFootageOverride: number | null;
  /** Footage frozen at send; null pre-send. */
  quoteLaborFootageFrozen: number | null;
  /** Resolved footage the charge is computed from (server-computed; read-only). */
  laborFootage: number;
  /**
   * Single per-job ACTUAL installed footage (migration 0056), LF gutter /
   * SF insulation by productLine. Null = not yet recorded. Last-write-wins
   * (office authoritative); set by crew (bot /hours) OR office (web Time card).
   * Phase 2a capture-only — no pay math hangs off it yet.
   */
  actualInstalledFootage: number | null;
  /** The labor charge in cents (server-computed; read-only). */
  quoteLaborChargeCents: number;
  /** Inclusive quote total = quoteAmountCents + tax + labor. Render THIS; do
   *  not recompute amount+tax client-side. (server-computed; read-only).
   *  NOTE: this app is tax-free — the tax term is 0, so this equals
   *  materials(post-discount) + labor charge. */
  quoteTotalCents: number;
  /** Effective labor COST rate applied (cents/unit, migration 0072): live = tenant
   *  cost, frozen = cost snapshot. Server-computed; read-only. For margin display. */
  laborCostRateCentsPerUnit: number;
  /** Labor cost = footage × cost rate (cents). NOT folded into quoteTotalCents
   *  (cost never affects the customer total). Server-computed; read-only. */
  quoteLaborCostCents: number;
  quotePdfR2Key: string | null;
  hasDraftPdf: boolean;
  hasFrozenPdf: boolean;
  quoteSentAt: string | null;
  quotedByCrewMemberId: string | null;
  /**
   * Set when this job was created from a manufactured-home template.
   * Null for hand-built jobs. Links to JobTemplate.id.
   */
  templateId: string | null;
  /**
   * Set when this job was created from a template — records which
   * color the office chose at creation. Null for non-template jobs.
   */
  templateColor: string | null;
  createdAt: string;
  updatedAt: string;
}

// ============================================================
// Job Templates (manufactured-home presets)
// ============================================================

export interface JobTemplate {
  id: string;
  tenantId: string;
  name: string;
  fixedPriceCents: number;
  displayOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SkuCategory {
  id: string;
  tenantId: string;
  name: string;
  defaultMarkupPct: number;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  // Aggregate populated by GET /api/sku-categories — count of SKUs that
  // reference this category. Optional because POST/PATCH responses don't
  // include it (the consumer would re-fetch the list anyway).
  skuCount?: number;
}

export interface Sku {
  id: string;
  tenantId: string;
  categoryId: string;
  code: string;
  name: string;
  variant: string | null;
  unit: SkuUnit;
  costCents: number;
  onHand: number;
  reorderThreshold: number;
  // Product attributes (Service Partners catalog)
  color: string | null;
  gauge: string | null;
  size: string | null;
  vendorSku: string | null;
  /** Service Partners 12-digit Part No (UPC/MPN), nullable (migration 0073). */
  partNo: string | null;
  priceCentsOverride: number | null;
  trackingLevel: TrackingLevel;
  active: boolean;
  /** Office-curated "most used" flag, drives the Common SKUs filter. */
  isCommon: boolean;
  /** Parent/child SKUs (migration 0069). A parent is an abstract coil profile
   *  (width × gauge × material) that is NEVER selectable/consumable. Children
   *  (color variants) link via parentSkuId. Standalone items: isParent=false,
   *  parentSkuId=null. */
  isParent: boolean;
  parentSkuId: string | null;
  /**
   * Square-foot coverage per unit (migration 0060). NULL for non-batting SKUs.
   * Insulation labor footage auto-derives = Σ(line.qty × coverage_sf) over the
   * job's batting lines (lines whose SKU has a non-null coverage_sf).
   */
  coverageSf: number | null;
  /** Insulation batting attributes (migration 0068), parsed from the name.
   *  Display/sort only; NULL for gutter + non-batting. rValue keeps the density
   *  modifier (R-15 / R-15HD); face ∈ UNF|KFT|SF; width/length in inches. */
  rValue: string | null;
  face: string | null;
  widthIn: number | null;
  lengthIn: number | null;
  /** Commercial / UoM layer + stored category-spec attributes (migration 0071).
   *  conversionFactor = consumption units per ONE purchase unit; USED in pricing
   *  (cost ÷ conversionFactor → price per consumptionUom — see computeSellPriceCents).
   *  conversionFactorSource ∈ import|coil_computed|override. */
  purchaseUom: string | null;
  consumptionUom: string | null;
  conversionFactor: number | null;
  conversionFactorSource: string;
  uomStatus: string | null;
  packQtyProposed: number | null;
  packUnitProposed: string | null;
  lbsPerLf: number | null;
  brand: string | null;
  typeAttr: string | null;
  style: string | null;
  material: string | null;
  coilProfile: string | null;
  coilTemper: string | null;
  gutterFaceSize: string | null;
  copperWeightOz: string | null;
  flatCoilWidth: string | null;
  thickness: string | null;
  lengthAttr: string | null;
  widthAttr: string | null;
  angle: string | null;
  offsetAttr: string | null;
  position: string | null;
  side: string | null;
  dim1: string | null;
  dim2: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SkuInventoryView {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  variant: string | null;
  unit: SkuUnit;
  trackingLevel: TrackingLevel;
  onHand: number;
  reorderThreshold: number;
  committed: number;
  available: number;
  needsReorder: boolean;
}

export interface FieldEstimate {
  id: string;
  tenantId: string;
  jobId: string;
  skuId: string | null;
  freehandText: string | null;
  quantity: number;
  status: FieldEstimateStatus;
  source: FieldEstimateSource;
  context: FieldEstimateContext;
  costCentsAtEstimate: number;
  priceCentsAtEstimate: number;
  submittedByCrewMemberId: string | null;
  createdAt: string;
  pulledAt: string | null;
  returnedAt: string | null;
  releasedAt: string | null;
}

export interface InventoryTxn {
  id: string;
  tenantId: string;
  skuId: string;
  delta: number;
  reason: InventoryTxnReason;
  unitCostCents: number | null;
  jobId: string | null;
  notes: string | null;
  createdAt: string;
  createdByEmail: string | null;
  createdByCrewMemberId: string | null;
}

// === Pricing engine I/O ===

export interface PricingLineItemInput {
  skuId: string;
  quantity: number;
}

/** Free-form MISC line as fed to the pricing engine (no SKU / markup / discount). */
export interface PricingMiscLineEngineInput {
  kind: 'misc';
  description: string;
  amountCents: number; // FLAT line total (sell = this, cost = 0)
  uom?: string | null; // informational
  quantity?: number | null; // informational
}

/** An engine line is a SKU line (no `kind`) OR a MISC line (`kind:'misc'`). SKU-only callers
 *  (quotes/create, bot) pass `PricingLineItemInput[]` unchanged. */
export type PricingEngineLine = PricingLineItemInput | PricingMiscLineEngineInput;

export interface PricingLineItemResult {
  skuId: string;
  skuCode: string;
  skuName: string;
  variant: string | null;
  unit: SkuUnit;
  quantity: number;
  /** Vendor cost per PURCHASE unit (purchaseUom). */
  costCents: number;
  markupPct: number;
  unitPriceCents: number;
  linePriceCents: number;
  /** Cost expressed per CONSUMPTION unit = costCents / conversionFactor (whole-bag
   *  insulation = costCents). Same unit as unitPriceCents, for the Cost column +
   *  margin. UN-rounded float. */
  costPerConsumptionCents: number;
  /** Consumption UOM label (purchaseUom→consumptionUom sell unit); falls back to `unit`. */
  consumptionUom: string;
}

/** Result row for a MISC line — its own "Miscellaneous" quote category. */
export interface PricingMiscLineResult {
  kind: 'misc';
  description: string;
  uom: string | null;
  quantity: number | null;
  /** = amountCents. sell = this; cost = 0; margin 100%. */
  linePriceCents: number;
  costCents: number; // always 0
}

/** The Miscellaneous section — parallel to Materials, DISCOUNT-EXEMPT. Empty when no misc. */
export interface PricingMiscSection {
  lines: PricingMiscLineResult[];
  sellCents: number; // Σ amountCents (undiscounted)
  costCents: number; // always 0
  marginPct: number; // always 100
}

export interface PricingResult {
  /** SKU lines only = the Materials category (shape UNCHANGED). */
  lineItems: PricingLineItemResult[];
  /** Free-form misc = the Miscellaneous category (discount-exempt). */
  miscellaneous: PricingMiscSection;
  /** materials sell (post-discount) + misc sell. INCLUDES UNDISCOUNTED misc — never recompute
   *  a total as `subtotal − discount`; use `totalCents`. */
  subtotalCents: number;
  customerDiscountPct: number;
  /** Discount on MATERIALS ONLY (misc is exempt). */
  customerDiscountCents: number;
  /** (materialsSubtotal − customerDiscountCents) + miscellaneous.sellCents. */
  totalCents: number;
}

/** A persisted free-form misc line on a job (table `job_misc_lines`). Returned by the job
 *  detail endpoint for rehydrate/display; `amountCents` is the flat line total. */
export interface JobMiscLine {
  id: string;
  description: string;
  uom: string | null;
  quantity: number | null;
  amountCents: number;
}

// === Auth context (set by Worker middleware) ===

export interface AuthContext {
  email: string;
  role: AppRole;
  tenantId: string;
}

// === Dashboard / Today view ===
//
// Single round-trip response for the back-office Today page. The page polls
// this every 10s, so the shape is intentionally one-call-fits-all rather than
// three separate fetches. See apps/worker/src/routes/dashboard.ts for the
// queries.

export interface DashboardLowInventoryRow {
  skuId: string;
  code: string;
  name: string;
  variant: string | null;
  unit: SkuUnit;
  onHand: number;
  committedToQuotes: number;
  effectivelyFree: number;
  reorderThreshold: number;
  /** reorderThreshold - effectivelyFree. Positive means we are short. */
  deficit: number;
}

export interface DashboardTodaysJobRow {
  jobId: string;
  displayName: string;
  address: string;
  status: JobStatus;
  customerId: string;
  customerName: string;
  customerType: CustomerType;
  quoteAmountCents: number | null;
  targetInstallDate: string | null;
}

/** A scheduled (non-job) task assigned to ≥1 person for today — shown on the
 *  back-office Today card alongside jobs (migration 0050 feature). */
export interface DashboardTodaysTaskRow {
  taskId: string;
  taskType: TaskType;
  taskDate: string;
  /** Assignee names (≥1 — the dashboard query is assigned-only). */
  peopleNames: string[];
  notes: string | null;
  done: boolean;
}

/** An estimate (measure) visit scheduled for today — shown on the Today card
 *  alongside install jobs. Per-assignment; `done` = the crew marked the
 *  measure visit complete (assignment completed_at). */
export interface DashboardTodaysEstimateRow {
  assignmentId: string;
  jobId: string;
  displayName: string;
  address: string;
  customerName: string;
  done: boolean;
}

/** A warranty follow-on visit overlapping today — shown on the Today card.
 *  `done` = visit marked complete (web or Telegram). */
export interface DashboardTodaysWarrantyRow {
  visitId: string;
  jobId: string;
  displayName: string;
  customerName: string;
  /** Assignee names (may be empty — warranty visits can be parked unassigned). */
  peopleNames: string[];
  done: boolean;
}

export interface DashboardOpenQuoteRow {
  jobId: string;
  displayName: string;
  customerId: string;
  customerName: string;
  quoteAmountCents: number;
  quoteSentAt: string | null;
  /** Hours since the quote was sent (or created if never sent). */
  ageHours: number;
}

/**
 * Crew-driven event surfaced to the back-office dashboard for
 * notification modals. Two kinds today:
 *
 *   - 'estimate_submitted' — crew submitted estimates via /estimate flow
 *     (status moved created→estimated; estimated_by_crew_member_id set)
 *   - 'install_completed' — crew tapped "Confirm install complete" in
 *     /schedule (status moved scheduled→installed; closed_by_crew_member_id set)
 *
 * Office's own dropdown PATCH explicitly clears the *_by_crew_member_id
 * column so it doesn't self-notify other office sessions. Server only
 * returns events from the last few minutes ("fire and forget" semantics).
 */
export type CrewActivityEventKind = 'estimate_submitted' | 'install_completed';

export interface DashboardCrewActivityRow {
  kind: CrewActivityEventKind;
  jobId: string;
  displayName: string;
  customerName: string;
  crewMemberName: string;
  /** ISO timestamp of the event. */
  timestamp: string;
}

/**
 * status='created' jobs with NO future estimate-purpose assignment.
 * Office needs to schedule a crew to go measure.
 */
export interface DashboardEstimateAlertRow {
  jobId: string;
  displayName: string;
  customerName: string;
  address: string;
  /** Hours since the job was created. Drives sort order (oldest first). */
  ageHours: number;
}

export interface DashboardTodayResponse {
  generatedAt: string;
  /** Top-N low SKUs by deficit (capped server-side — see LOW_INVENTORY_LIMIT in
   *  dashboard.ts). Use lowInventoryTotal for the real count. */
  lowInventory: DashboardLowInventoryRow[];
  /** Total count of low SKUs (the array above is capped for egress). */
  lowInventoryTotal: number;
  todaysJobs: DashboardTodaysJobRow[];
  /** Today's scheduled tasks with ≥1 assignee (migration 0050). */
  todaysTasks: DashboardTodaysTaskRow[];
  /** Estimate (measure) visits scheduled for today. */
  todaysEstimates: DashboardTodaysEstimateRow[];
  /** Warranty follow-on visits overlapping today. */
  todaysWarranty: DashboardTodaysWarrantyRow[];
  openQuotes: DashboardOpenQuoteRow[];
  /** Recent crew-driven events (estimate submitted, install completed) — last few minutes. */
  crewActivity: DashboardCrewActivityRow[];
  /** Jobs stuck at 'created' with no estimate visit scheduled. */
  awaitingEstimate: DashboardEstimateAlertRow[];
  /** All jobs in 'invoiced' status — money still out there, no time threshold. */
  awaitingPayments: DashboardAgingRow[];
}

/**
 * Row returned for aging alerts — either estimated-too-long or
 * awaiting_payment-too-long. Days-in-stage is precomputed server-side
 * against the relevant timestamp column (estimated_at / awaiting_payment_at)
 * so the frontend doesn't need to know which column drove the count.
 */
export interface DashboardAgingRow {
  jobId: string;
  displayName: string;
  address: string;
  customerId: string;
  customerName: string;
  quoteAmountCents: number | null;
  /** Whole days the job has been in this stage. */
  daysInStage: number;
}

// === Inventory page ===
//
// Returned by GET /api/inventory/skus. Each row is a SKU enriched with
// the same effectively_free / committed_to_quotes math as the dashboard
// low-inventory query, plus category name and an owner-gated cost field.

export interface InventorySkuRow {
  skuId: string;
  code: string;
  name: string;
  variant: string | null;
  unit: SkuUnit;
  categoryId: string;
  categoryName: string;
  trackingLevel: TrackingLevel;
  active: boolean;
  /** Office-curated "most used" flag — drives the Common SKUs filter. */
  isCommon: boolean;
  /** Parent (abstract coil profile) — NEVER selectable/consumable. Gated out of
   *  every picker + the bot; the Inventory page groups children under it. */
  isParent: boolean;
  /** Child → parent link (migration 0069); NULL for parents + standalone items. */
  parentSkuId: string | null;
  onHand: number;
  committedToQuotes: number;
  effectivelyFree: number;
  reorderThreshold: number;
  /** Cents. Only populated when the requester is an owner. */
  costCents: number | null;
  isLowStock: boolean;
  /** SF/bag coverage (migration 0060); NULL for non-batting SKUs. Surfaced so
   *  the SKU quickview can show/edit it without fetching the full Sku. */
  coverageSf: number | null;
  /** Service Partners 12-digit Part No (migration 0073). Surfaced so the SKU
   *  detail sidecard can show the "Part #" line without fetching the full Sku;
   *  NOT rendered as an Inventory table column. */
  partNo: string | null;
  /** Insulation batting attributes (migration 0068); NULL for non-batting.
   *  Surfaced for the Insulation-view columns + the detail panel. */
  rValue: string | null;
  face: string | null;
  widthIn: number | null;
  lengthIn: number | null;
  /** Commercial / UoM layer (migration 0071). conversionFactor = consumption units
   *  per ONE purchase unit (e.g. coil LB→LF); editable per SKU (Operational tab),
   *  shown in Commercial. USED in pricing: cost_cents ÷ conversionFactor → price per
   *  consumptionUom, then category markup (see computeSellPriceCents). Whole-bag
   *  insulation (coverageSf > 0) is NOT divided. conversionFactorSource ∈
   *  import|coil_computed|override. uomStatus flags data quality; packQtyProposed
   *  and lbsPerLf feed the pending-pack-qty + coil-weight follow-ups. */
  purchaseUom: string | null;
  consumptionUom: string | null;
  conversionFactor: number | null;
  conversionFactorSource: string;
  uomStatus: string | null;
  packQtyProposed: number | null;
  packUnitProposed: string | null;
  lbsPerLf: number | null;
  /** Gutter Coil attributes — STORED column (migration 0071), parser fallback for
   *  rows not yet imported. NULL for non-coil rows. */
  coilSize: string | null;
  coilStyle: string | null;
  coilColor: string | null;
  coilWidth: string | null;
  coilThickness: string | null;
  coilMaterial: string | null;
  /** Gutter Coil master-data columns (migration 0071) surfaced for the Catalog
   *  view's per-category columns. coilProfile = K-Style/Half-Round/etc;
   *  gutterFaceSize = the visible face dimension; flatCoilWidth = the flat stock
   *  width; copperWeightOz = copper coil weight. NULL for non-coil rows. */
  coilProfile: string | null;
  gutterFaceSize: string | null;
  flatCoilWidth: string | null;
  // Stored copper coil weight, e.g. "16 oz" (TEXT in migration 0071). Kept as the
  // raw stored string — the Catalog "Cu Oz" column renders it directly. (Was
  // number|null, but the stored value carries the unit, so Number() → NaN.)
  copperWeightOz: string | null;
  // Stored coil temper (Soft / 1/2 Hard / 3/4 Hard), copper-only (migration 0071,
  // CSV `temper`). Surfaced in the Catalog "Temper" column + cascade filter. NULL
  // for non-copper / non-coil rows. STORED — never derived.
  coilTemper: string | null;
  /** Downspout attributes — DERIVED on read (extractDownspoutAttrs) from the
   *  name + size/color/gauge; not stored. NULL for non-downspout rows. */
  downspoutStyle: string | null;
  downspoutConnectorSize: string | null;
  downspoutLength: string | null;
  downspoutMaterial: string | null;
  downspoutColor: string | null;
  /** Elbow attributes — DERIVED on read (extractElbowAttrs) from the name +
   *  size/color; not stored. NULL for non-elbow rows. */
  elbowStyle: string | null;
  elbowConnectorSize: string | null;
  elbowType: string | null;
  elbowAngle: string | null;
  elbowOffset: string | null;
  elbowMaterial: string | null;
  elbowColor: string | null;
  /** Miter attributes — DERIVED on read (extractMiterAttrs) from the name +
   *  size/color; not stored. NULL for non-miter rows. */
  miterStyle: string | null;
  miterType: string | null;
  miterPosition: string | null;
  miterSize: string | null;
  miterMaterial: string | null;
  miterColor: string | null;
  /** End Cap attributes — DERIVED on read (extractEndCapAttrs) from the name +
   *  size/color; not stored. NULL for non-endcap rows. */
  endcapStyle: string | null;
  endcapSize: string | null;
  endcapSide: string | null;
  endcapMaterial: string | null;
  endcapColor: string | null;
  /** Gutter Guard attributes — DERIVED on read (extractGuardAttrs) from the name +
   *  size/color; not stored. NULL for non-guard rows. */
  guardBrand: string | null;
  guardType: string | null;
  guardWidth: string | null;
  guardLength: string | null;
  guardMaterial: string | null;
  guardColor: string | null;
  /** Outlet attributes — DERIVED on read (extractOutletAttrs). NULL for non-outlet rows. */
  outletBrand: string | null;
  outletType: string | null;
  outletSize: string | null;
  outletMaterial: string | null;
  outletColor: string | null;
  /** Adapter & Conductor attributes — DERIVED on read (extractAdapterConductorAttrs).
   *  NULL for non-adapter rows. Dim1 = inlet/top, Dim2 = outlet/bottom. */
  adapterBrand: string | null;
  adapterType: string | null;
  adapterStyle: string | null;
  adapterDim1: string | null;
  adapterDim2: string | null;
  adapterMaterial: string | null;
  adapterColor: string | null;
  /** Hanger & Pipe Band attributes — DERIVED on read (extractHangerAttrs). NULL
   *  for non-hanger rows. Type leads (heterogeneous category); Style is the
   *  hanger mechanism (null for pipe hardware). */
  hangerBrand: string | null;
  hangerType: string | null;
  hangerStyle: string | null;
  hangerSize: string | null;
  hangerMaterial: string | null;
  hangerColor: string | null;
  /** Wedge & Shank attributes — DERIVED on read (extractWedgeShankAttrs). NULL for
   *  non-wedge/shank rows. Type = T-Wedge|Wedge|Shank|Combination; Size = 5"/6" or #N. */
  wedgeShankBrand: string | null;
  wedgeShankType: string | null;
  wedgeShankSize: string | null;
  wedgeShankMaterial: string | null;
  wedgeShankColor: string | null;
  /** Fastener attributes — DERIVED on read (extractFastenerAttrs). NULL for
   *  non-fastener rows. Type = Screw|Bolt|Rivet|Nail; Gauge/Length = the spec. */
  fastenerType: string | null;
  fastenerGauge: string | null;
  fastenerLength: string | null;
  fastenerColor: string | null;
  /** Sealer & Paint attributes — DERIVED on read (extractSealerPaintAttrs). NULL
   *  for non-sealer/paint rows. Type = full sub-type; Color = local finish vocab. */
  sealerPaintType: string | null;
  sealerPaintBrand: string | null;
  sealerPaintSize: string | null;
  sealerPaintColor: string | null;
  /** Tools & Equipment attributes — DERIVED on read (extractToolAttrs). NULL for
   *  non-tool rows. toolType = the tool kind (column LABEL is "Tool"). */
  toolType: string | null;
  toolSize: string | null;
}

export interface InventoryListResponse {
  skus: InventorySkuRow[];
  /** Total active SKU count, regardless of filter. */
  totalSkus: number;
  /** How many active SKUs are at or below their reorder threshold. */
  lowStockCount: number;
}

// Returned by GET /api/inventory/txns?skuId=X. Recent transaction history
// for ONE SKU, used by the inventory detail side panel.
//
// unitCostCents is owner-only — null when caller is back-office, or when
// the original txn had no cost (e.g. count corrections).

export interface InventoryTxnRow {
  id: string;
  skuId: string;
  delta: number;
  reason: InventoryTxnReason;
  unitCostCents: number | null;
  jobId: string | null;
  notes: string | null;
  createdAt: string;
  createdByEmail: string | null;
}

// === Quote create response ===

export interface QuoteCreateResponse {
  jobId: string;
  quoteAmountCents: number;
  lineItemCount: number;
}

// === Jobs page ===
//
// JobListRow is what GET /api/jobs returns — basic Job fields plus a
// denormalized customer name (and type) so the list table can render
// "Patel residence — Robert & Linda Patel · residential" without a
// per-row fetch. customerId is already in Job.

export interface JobListRow extends Job {
  customerName: string;
  customerType: CustomerType;
}

// JobDetail is what GET /api/jobs/:id returns — the basic Job plus the
// full customer object plus the field estimates (line items) for this
// job, with SKU info denormalized inline. Cost-bearing totals are
// owner-gated (back-office sees null per the discovery rule).

export interface JobFieldEstimateRow {
  id: string;
  skuId: string | null;
  skuCode: string | null;
  skuName: string | null;
  variant: string | null;
  unit: SkuUnit | null;
  /** Sell UOM (consumptionUom) for display, e.g. "LF"/"SF"/"EA". NULL if unset. */
  consumptionUom: string | null;
  /** SF/unit of the line's SKU (migration 0060); NULL for non-batting. Drives
   *  the per-line insulation SF breakdown + the derived labor footage. */
  coverageSf: number | null;
  /** Insulation batting only (migration 0067): the actual SF the office entered;
   *  `quantity` holds the ceil-rounded bags. NULL for legacy/bag-entered/non-
   *  batting lines. Labor prefers this; display shows "<enteredSf> SF → <qty> bags". */
  enteredSf: number | null;
  freehandText: string | null;
  quantity: number;
  status: FieldEstimateStatus;
  source: FieldEstimateSource;
  context: FieldEstimateContext;
  /** Sale price at the time the estimate was made — visible to all roles. */
  priceCentsAtEstimate: number;
  /** Cost basis at the time of the estimate — owner only. */
  costCentsAtEstimate: number | null;
  /** Per-line SF/bag coverage override for shared-bag insulation presets (migration
   *  0074); overrides the SKU coverage_sf for SF→bags + labor footage. NULL = SKU's. */
  coverageSfOverride: number | null;
  /** Per-line R-value label for shared-bag presets, so multiple lines on one SKU
   *  render distinctly. NULL otherwise. */
  rValue: string | null;
  createdAt: string;
}

export interface JobDetail extends Job {
  customer: {
    id: string;
    name: string;
    type: CustomerType;
    email: string | null;
    phone: string | null;
    discountPct: number;
  };
  fieldEstimates: JobFieldEstimateRow[];
  /** Free-form Miscellaneous quote lines (migration 0075). Discount-exempt; each amountCents
   *  is a flat line total. Present on any non-completed job; empty otherwise. */
  miscLines: JobMiscLine[];
  /** Σ miscLines.amountCents — the misc contribution already folded into quoteTotalCents. */
  miscTotalCents: number;
  /** Sum of priceCentsAtEstimate across all confirmed estimates. */
  estimatedPriceCents: number;
  /** Sum of costCentsAtEstimate across all confirmed estimates — owner only. */
  estimatedCostCents: number | null;
}

// === Calendar / Scheduling ===

export interface CrewRow {
  id: string;
  name: string;
  color: string;
  displayOrder: number;
  active: boolean;
}

export type AssignmentPurpose = 'install' | 'estimate';

/** Fixed set of non-job schedule items (migration 0050). Hard-coded;
 *  add a value here + in the DB CHECK (scheduled_tasks_type_valid) to
 *  introduce a 5th. */
export type TaskType =
  | 'physical_inventory'
  | 'receive_delivery'
  | 'truck_maintenance'
  | 'contact_danny';

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  physical_inventory: 'Physical Inventory',
  receive_delivery: 'Receive Delivery',
  truck_maintenance: 'Truck Maintenance',
  contact_danny: 'Contact Danny',
};

/** All four task types in display order (for pickers + exhaustiveness). */
export const TASK_TYPES: TaskType[] = [
  'physical_inventory',
  'receive_delivery',
  'truck_maintenance',
  'contact_danny',
];

/** A scheduled task as the calendar consumes it. Single-day, no
 *  customer/job. Surfaced via GET /api/calendar/tasks (parallel to the
 *  jobs assignments feed); the calendar discriminates job vs task in the
 *  UI layer (no `kind` on the wire). */
export interface CalendarTaskRow {
  id: string;
  taskType: TaskType;
  /** Single day (YYYY-MM-DD). */
  taskDate: string;
  notes: string | null;
  /** Stamped when marked done (web or Telegram). Calendar dims + strikes. */
  completedAt: string | null;
  /** People assigned (from scheduled_task_people). May be empty (task
   *  parked unassigned → renders in the "(no people yet)" swimlane). */
  people: { id: string; name: string }[];
}

// === Warranty visits (migration 0051) ===
// Follow-on warranty site visits hung off an installed job, date-ranged,
// per-row completion (no status machine). DISTINCT from job_type='warranty'
// (a whole-job classification). See migration 0051 header.

/** A warranty visit as the JobDetail Warranty card consumes it (the job is
 *  already known, so no denormalized job label). Date-RANGED. */
/** A document attached to a job (per-job library; PDF/DOCX/XLSX/images).
 *  Bytes live in R2; this is the metadata the API returns. The r2_key
 *  pointer is intentionally NOT exposed — downloads go through
 *  GET /api/jobs/:jobId/documents/:id. */
export interface JobDocument {
  id: string;
  fileName: string;
  /** Optional free-text label (not a fixed category). */
  label: string | null;
  mimeType: string;
  sizeBytes: number;
  /** 'web' (office) or 'telegram' (crew). */
  source: 'web' | 'telegram';
  /** Display name of the uploader (app_user.display_name or crew_member.name),
   *  null if unresolved. */
  uploadedByName: string | null;
  /** ISO timestamp. */
  createdAt: string;
}

// === Time entries (migration 0056; Crew Utilization rework 0057) ===
// Per-person TRAVEL hours for a job on a day (install_hours dropped in 0057).
// Footage is a single per-job value (jobs.actualInstalledFootage) credited to
// every crew member on the job — not captured per entry. CAPTURE ONLY — no
// pay-dollar math (that's Phase 2b "Q-PAY"). One row per (job, crew_member,
// work_date); re-logging replaces it.

/** A time entry as the office Job Detail "Time" card consumes it (the job is
 *  already known). camelCase mirror of the DB row, with the worker's
 *  hours coerced to numbers and the person's name denormalized for display. */
export interface TimeEntry {
  id: string;
  jobId: string;
  /** Informational/derived phase pointer (insulation; null for gutter). */
  phaseId: string | null;
  crewMemberId: string;
  /** Display name of the crew member (denormalized for list rows). */
  personName: string;
  /** Workday (YYYY-MM-DD). */
  workDate: string;
  travelHours: number;
  /** ISO timestamps. */
  createdAt: string;
  updatedAt: string;
}

/** One install assignment's ACTUAL footage capture row, as the office Job
 *  Detail footage editor consumes it (migration 0065). Gutter jobs have one
 *  (or, if date-segmented, more) rows with phaseId null; insulation has one
 *  per phase. plannedFootage is the soft prefill (gutter ft-sum / insulation
 *  Σ coverage_sf, same basis as the quote). actualInstalledFootage is the
 *  captured value (null = not yet captured). */
export interface InstallAssignmentFootage {
  id: string;
  /** Insulation phase pointer; null for gutter. */
  phaseId: string | null;
  /** 1-indexed phase ordinal (always 1 for gutter). */
  phaseNumber: number;
  /** Soft prefill — that assignment's PLANNED footage (LF gutter / SF insulation). */
  plannedFootage: number;
  /** Captured actual footage; null until set. */
  actualInstalledFootage: number | null;
}

/** Office footage edit body (PATCH /api/jobs/:jobId/assignments/:id). null clears. */
export interface AssignmentFootageUpdateInput {
  actualInstalledFootage: number | null;
}

/** Office add-entry body (POST /api/jobs/:jobId/time-entries). phaseId is
 *  server-derived from the job+date install assignment, not client-supplied. */
export interface TimeEntryCreateInput {
  crewMemberId: string;
  /** Workday (YYYY-MM-DD). */
  workDate: string;
  travelHours: number;
}

/** Office inline-edit body (PATCH /api/jobs/:jobId/time-entries/:id). Any
 *  subset; identity (crewMemberId/workDate) is fixed once created. */
export interface TimeEntryUpdateInput {
  travelHours?: number;
}

// === Crew Utilization analytics (read-only rollups over time_entries) ===

/** One crew member's CREDITED utilization over a date range. travelHours = SUM
 *  of their travel; lfFootage/sfFootage = footage of the DISTINCT jobs they
 *  worked, credited IN FULL and split by product line (gutter=LF, insulation=SF
 *  — never summed together). jobCount/activeDays are distinct counts. Credited
 *  footage is NEVER grand-totaled across crews (see TeamPeriodTotals). */
export interface CrewPeriodRow {
  crewMemberId: string;
  crewMemberName: string;
  travelHours: number;
  lfFootage: number;
  sfFootage: number;
  jobCount: number;
  activeDays: number;
}

/** Team ACTUAL totals over a date range. travelHours = team-wide SUM; lf/sf are
 *  the REAL production figures — DISTINCT jobs counted ONCE (NOT the credited
 *  column sum); jobCount/activeDays distinct team-wide. Feeds the KPI tiles. */
export interface TeamPeriodTotals {
  travelHours: number;
  lfFootage: number;
  sfFootage: number;
  jobCount: number;
  activeDays: number;
}

/** GET /api/crew-utilization?weekFrom&weekTo&monthFrom&monthTo response. */
export interface CrewUtilizationResponse {
  byCrewWeek: CrewPeriodRow[];
  byCrewMonth: CrewPeriodRow[];
  teamWeek: TeamPeriodTotals;
  teamMonth: TeamPeriodTotals;
}

/** One point in the monthly LF/SF chart (GET /api/crew-utilization/series).
 *  Footage bucketed to each job's COMPLETION month (MAX(work_date)) so a
 *  boundary-spanning job is counted in exactly ONE month. month is 1–12. */
export interface MonthlyFootagePoint {
  month: number;
  lfFootage: number;
  sfFootage: number;
}

/** One of a person's jobs in the drawer drill (GET .../person/:id). footage =
 *  the job's actualInstalledFootage (unit by productLine). One row per job. */
export interface CrewPersonJobRow {
  jobId: string;
  displayName: string;
  customerName: string;
  productLine: ProductLine;
  travelHours: number;
  footage: number;
  activeDays: number;
  /** First/last work_date for this person on this job (YYYY-MM-DD). */
  firstDate: string;
  lastDate: string;
}

/** GET /api/crew-utilization/person/:crewMemberId?from&to response. totals are
 *  the person's CREDITED rollup over the range. */
export interface CrewPersonDetail {
  crewMemberName: string;
  totals: {
    travelHours: number;
    lfFootage: number;
    sfFootage: number;
    jobCount: number;
    activeDays: number;
  };
  jobs: CrewPersonJobRow[];
}

export interface WarrantyVisitRow {
  id: string;
  /** Inclusive start day (YYYY-MM-DD). start=end for a single-day visit. */
  visitDateStart: string;
  /** Inclusive end day (YYYY-MM-DD). */
  visitDateEnd: string;
  notes: string | null;
  /** Stamped when marked done (web or Telegram). Card dims + strikes. */
  completedAt: string | null;
  /** People assigned (from warranty_visit_people). May be empty. */
  people: { id: string; name: string }[];
}

/** A warranty visit as the CALENDAR consumes it — adds the denormalized job
 *  label so the pill reads like a job (visits surface on the main calendar
 *  via GET /api/calendar/warranty, a 3rd feed parallel to assignments/tasks). */
export interface CalendarWarrantyVisitRow extends WarrantyVisitRow {
  jobId: string;
  customerName: string;
  address: string;
}

/** Inclusive calendar-day count for one warranty visit:
 *  (end - start + 1). Parsed as UTC midnights so the diff is DST-safe.
 *  Counts CALENDAR days (a Fri–Mon visit = 4), not business days — per the
 *  chosen "auto from date range" tracking option. THIS IS THE SINGLE
 *  DEFINITION of a warranty workday; the worker does not re-derive it in SQL. */
export function warrantyWorkdays(visitDateStart: string, visitDateEnd: string): number {
  const start = Date.parse(`${visitDateStart}T00:00:00Z`);
  const end = Date.parse(`${visitDateEnd}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.round((end - start) / 86_400_000) + 1;
}

/** Total warranty workdays on a project site = sum across all its visits. */
export function warrantyWorkdaysTotal(
  visits: ReadonlyArray<{ visitDateStart: string; visitDateEnd: string }>,
): number {
  return visits.reduce((sum, v) => sum + warrantyWorkdays(v.visitDateStart, v.visitDateEnd), 0);
}

export interface CalendarAssignmentRow {
  id: string;
  /** Inclusive start day of the install range (or estimate visit day).
   *  For single-day assignments, equals assignedDateEnd. */
  assignedDateStart: string;
  /** Inclusive end day of the install range. Estimates are always
   *  single-day, so this equals assignedDateStart for purpose='estimate'. */
  assignedDateEnd: string;
  notes: string | null;
  /** 'install' = scheduled work visit (default). 'estimate' = pre-install
   *  measure/sketch visit. Drives per-crew filtering on Telegram. */
  purpose: AssignmentPurpose;
  /** Stamped when the purpose action finishes (estimate submitted OR
   *  job installed). Null while pending. Calendar renders completed
   *  blocks with a checkmark + dim styling. */
  completedAt: string | null;
  /** Phase 2.4: `crew` is the REPRESENTATIVE preset of the assignment's
   *  people (first person's first preset), retained purely for calendar
   *  coloring. id='' / color gray when no person has a preset. The
   *  authoritative "who's on this" surface is `people`. */
  crew: { id: string; name: string; color: string };
  /** Phase 2.1+: the people assigned to this row (from assignment_people).
   *  Phase 2.2 UI renders + edits this instead of `crew`. */
  people: { id: string; name: string }[];
  job: {
    id: string;
    displayName: string;
    address: string;
    status: string;
    customerName: string;
    quoteAmountCents: number | null;
    /** True when the job already has a purpose='install' assignment. Locks a
     *  completed estimate's revert (client hides it; worker rejects it) so the
     *  estimate the install was scheduled off of can't be un-done. */
    hasInstallAssignment: boolean;
  };
  /** Migration 0043 enrichment. Populated for insulation rows (the
   *  assignment is linked to a phases row); null for gutter rows. The
   *  three schedule surfaces (Phases card, side drawer, Calendar) render
   *  the phase pill + items tooltip from this field, so all three agree
   *  on what's scheduled. itemsSummary follows
   *  COALESCE(sku.name, fe.freehand_text, '(unspecified)') ordered by
   *  planned_quantity DESC, top 3 + "and N more". */
  phase?: {
    id: string;
    phaseNumber: number;
    itemCount: number;
    itemsSummary: string;
    /** Phase 1.2 Item 8: structured items list ordered by quantity DESC,
     *  capped at top-20 server-side. Used by the calendar day modal to
     *  render `Name × Qty Unit` lines for each item. Absent when the
     *  phase row has no items (defensive — shouldn't fire because
     *  handleCreatePhase requires items.length >= 1). */
    items?: Array<{ name: string; quantity: number; unit: string }>;
  } | null;
}

export interface CalendarUnassignedJob {
  id: string;
  displayName: string;
  address: string;
  status: string;
  customerName: string;
  quoteAmountCents: number | null;
}

/**
 * GET /api/calendar/unassigned response.
 *
 * Split into two pools based on what scheduling step is missing:
 *   - installs: status='accepted' jobs that have no install-purpose assignment
 *     (ready for install scheduling)
 *   - estimates: status='created' jobs that have no estimate-purpose assignment
 *     (ready for estimate-visit scheduling)
 *
 * Calendar sidebar renders these as two separate sections/tabs.
 */
export interface CalendarUnassignedResponse {
  installs: CalendarUnassignedJob[];
  estimates: CalendarUnassignedJob[];
}
